import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { splitTextVariants, splitCharVariants, fadeInUp, useParallaxScroll } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Button from './Button';
import D3Background from './D3Background';
import LazyImage from './LazyImage';

const SplitText = ({ children, className = '' }) => {
  const words = children.split(' ');
  
  return (
    <motion.div
      className={className}
      variants={splitTextVariants}
      initial="hidden"
      animate="visible"
    >
      {words.map((word, wordIndex) => (
        <motion.span key={wordIndex} className="inline-block mr-3">
          {word.split('').map((char, charIndex) => (
            <motion.span
              key={charIndex}
              className="inline-block"
              variants={splitCharVariants}
            >
              {char}
            </motion.span>
          ))}
        </motion.span>
      ))}
    </motion.div>
  );
};

const Hero = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const { updateParallax } = useParallaxScroll();
  const [waveTime, setWaveTime] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageStartTime, setImageStartTime] = useState(Date.now());
  const animationFrameRef = useRef();
  
  const backgroundImages = useMemo(() => [
    '/background/bck (1).jpg',
    '/background/bck (2).jpg',
    '/background/bck (3).jpg',
    '/background/bck (4).jpg',
    '/background/bck (5).jpg',
    '/background/bck (6).jpg',
    '/background/bck (7).jpg',
    '/background/bck (8).jpg'
  ], []);

  const [scrollY, setScrollY] = useState(0);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
  
  useEffect(() => {
    const handleScroll = () => {
      updateParallax();
      setScrollY(window.scrollY);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateParallax]);
  
  // Wave animation timing
  useEffect(() => {
    const animate = () => {
      setWaveTime(Date.now() * 0.001);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Remove preloading - LazyImage will handle lazy loading

  // Sync background changes with zoom completion
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex + 1) % backgroundImages.length
      );
      setImageStartTime(Date.now()); // Reset zoom timer
    }, 10000); // 10 seconds for full zoom cycle

    return () => clearInterval(interval);
  }, [backgroundImages.length]);
  
  const handleScrollToNext = () => {
    const nextSection = document.querySelector('#about');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className={`relative min-h-screen flex items-center justify-center overflow-hidden ${colors.bg.primary} transition-colors duration-300`} style={{ paddingTop: '0px', marginTop: '-80px', zIndex: 20 }}>
      {/* D3.js Animated Background */}
      <D3Background className="z-10" intensity={0.3} />

      {/* Carousel Background - Extended behind navbar */}
      <div className="absolute -inset-x-0 -top-20 bottom-0 z-5">
        {/* Background Image Carousel with LazyImage */}
        <div className="absolute inset-0 w-full h-full">
          {backgroundImages.map((image, index) => {
            // Calculate wave-based animation timing
            const waveOffset1 = Math.sin(waveTime * 0.8) * 20;
            const waveOffset2 = Math.cos(waveTime * 1.2) * 15;
            const waveScale = 1 + Math.sin(waveTime * 0.6) * 0.02;

            // Calculate progressive zoom based on time since image became active
            const isActive = index === currentImageIndex;
            const timeSinceStart = (Date.now() - imageStartTime) / 1000; // Convert to seconds
            const zoomProgress = Math.min(timeSinceStart / 8, 1); // 8 seconds to reach full zoom
            const zoomScale = isActive ? (1.0 + zoomProgress * 0.3) : 1.0; // Zoom from 1.0 to 1.3x

            return (
              <LazyImage
                key={image}
                src={image}
                alt={`Steel manufacturing background ${index + 1}`}
                className="parallax-carousel-bg absolute inset-0 w-full h-full object-cover"
                style={{
                  transform: isDesktop
                    ? `translateY(${scrollY * 0.4 + waveOffset1}px) translateX(${waveOffset2}px) scale(${(zoomScale + scrollY * 0.0003) * waveScale})`
                    : `scale(${zoomScale * waveScale}) translateX(${waveOffset2 * 0.5}px)`,
                  opacity: isActive ? 0.7 : 0,
                  filter: `brightness(${isActive ? 1 : 0.95}) hue-rotate(${Math.sin(waveTime * 0.3) * 5}deg)`,
                  transition: isActive
                    ? 'opacity 1500ms cubic-bezier(0.4, 0.0, 0.2, 1), filter 1500ms cubic-bezier(0.4, 0.0, 0.2, 1)' // No scale transition for active image
                    : 'opacity 1500ms cubic-bezier(0.4, 0.0, 0.2, 1), filter 1500ms cubic-bezier(0.4, 0.0, 0.2, 1), transform 1500ms cubic-bezier(0.4, 0.0, 0.2, 1)',
                  willChange: 'transform, opacity, filter',
                  zIndex: isActive ? 2 : 1,
                  mixBlendMode: 'normal'
                }}
                threshold={0.5} // Load when 50% in view
              />
            );
          })}
        </div>

      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" style={{ paddingTop: '80px' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Headline with split-text animation */}
          <SplitText className={`text-4xl md:text-6xl lg:text-7xl font-bold font-orbitron ${colors.text.primary} mb-6 leading-tight`}>
            Precision Steel. Reliable Supply.
          </SplitText>

          {/* Subheading */}
          <motion.p
            className={`text-xl md:text-2xl ${colors.text.secondary} mb-8 max-w-3xl mx-auto leading-relaxed`}
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 1.2 }}
          >
            Julfikar Steel is a modern steel re-rolling mill delivering consistent quality 
            for infrastructure and industry.
          </motion.p>

          {/* Key value proposition */}
          <motion.div
            className="flex flex-wrap justify-center gap-6 mb-12 text-sm md:text-base"
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 1.4 }}
          >
            {[
              'Consistent mechanical properties',
              'On-time delivery',
              'Competitive lead times',
              'Quality you can verify'
            ].map((item, index) => (
              <motion.div
                key={item}
                className={`flex items-center ${colors.text.secondary}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 + (index * 0.1) }}
              >
                <div className="w-2 h-2 bg-primary-400 rounded-full mr-3"></div>
                {item}
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 1.6 }}
          >
            <Button 
              variant="primary" 
              size="lg" 
              magnetic={true}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Request a Quote
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => document.querySelector('#capabilities')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Capabilities
            </Button>
          </motion.div>

        </motion.div>
      </div>

      {/* Tagline overlay */}
      <motion.div
        className="absolute bottom-20 right-8 text-right z-20"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.2, duration: 0.6 }}
      >
        <p className="text-primary-400 font-semibold text-lg">
          Steel you can count on.
        </p>
      </motion.div>
    </section>
  );
};

export default Hero;
